/**
 * 📬 NOTIFICATION SERVICE - Multi-Channel Notification Delivery System
 * 
 * Provides comprehensive notification management with multi-channel delivery,
 * read/unread tracking, and intelligent batching to reduce notification fatigue.
 * 
 * Features:
 * - Multi-channel delivery (in-app, email, SMS, push)
 * - Read/unread tracking with timestamps
 * - Notification grouping (batch related notifications)
 * - Priority-based delivery (critical notifications bypass batching)
 * - Notification preferences per user
 * - Delivery confirmation tracking
 * - Automatic cleanup of old read notifications
 * - Tenant isolation (all notifications scoped to company)
 * 
 * SECURITY: Every operation validates tenant context to prevent cross-tenant data leakage.
 * 
 * @module notificationService
 * @author OilWellX Engineering
 * @version 2.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  writeBatch,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId, isSuperAdmin } from '../tenantContext';
import { guardedCreate, guardedUpdate, guardedDelete } from '../firebase/writeGuard';
import { FIRESTORE_PATHS } from '../firebase/paths';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Notification types (categorizes what triggered the notification)
 */
export type NotificationType =
  | 'alert'                     // From alert system
  | 'ticket_assigned'           // Service ticket assigned to you
  | 'ticket_updated'            // Ticket you're watching was updated
  | 'ticket_resolved'           // Ticket you created was resolved
  | 'route_assigned'            // Route assigned to you
  | 'route_changed'             // Your route was modified
  | 'production_entry_reviewed' // Your production entry was reviewed/approved
  | 'approval_required'         // Manager approval needed
  | 'approval_granted'          // Your request was approved
  | 'approval_denied'           // Your request was denied
  | 'time_clock_alert'          // Forgot to clock out, etc.
  | 'inventory_alert'           // Parts inventory notification
  | 'permit_expiring'           // Permit expiration reminder
  | 'inspection_due'            // Inspection due soon
  | 'invoice_ready'             // Invoice generated
  | 'payment_received'          // Payment confirmation
  | 'user_mention'              // You were mentioned in comment
  | 'system_announcement'       // System-wide announcement
  // ─────────────────────────────────────────────────────────────────────────
  // WELL ASSIGNMENT NOTIFICATIONS (Task #17 - from wellAssignment.ts)
  // ─────────────────────────────────────────────────────────────────────────
  | 'well_assigned'             // Pumper assigned to a new well
  | 'well_unassigned'           // Pumper removed from a well
  | 'bulk_wells_assigned'       // Multiple wells assigned at once
  | 'coverage_started'          // Temporary coverage began
  | 'coverage_ending'           // Coverage ending soon (1-day warning)
  | 'coverage_ended'            // Coverage period completed
  | 'coverage_reminder'         // Reminder during active coverage
  | 'backup_activated'          // Backup pumper taking over
  | 'backup_deactivated'        // Primary pumper resumed
  | 'orphan_wells_detected'     // Wells without pumper assignment (manager alert)
  | 'custom';                   // User-defined custom notification

/**
 * Notification priority (affects delivery urgency)
 */
export type NotificationPriority = 'urgent' | 'high' | 'normal' | 'low';

/**
 * Delivery channels
 */
export type DeliveryChannel = 'in_app' | 'email' | 'sms' | 'push';

/**
 * Delivery status per channel
 */
export interface DeliveryStatus {
  channel: DeliveryChannel;
  sent: boolean;
  sentAt?: Date;
  deliveredAt?: Date;
  error?: string;
}

/**
 * Action button in notification
 */
export interface NotificationAction {
  label: string;
  action: 'navigate' | 'api_call' | 'dismiss';
  url?: string;
  apiEndpoint?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  payload?: Record<string, unknown>;
}

/**
 * Complete Notification document structure
 */
export interface Notification {
  id: string;
  companyId: string;
  
  // Classification
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  
  // Recipient
  userId: string;              // Primary recipient
  recipientId: string;         // Alias for userId (for consistency with alerts)
  
  // Delivery
  channels: DeliveryChannel[];
  deliveryStatus: DeliveryStatus[];
  
  // Read tracking
  isRead: boolean;
  readAt?: Date;
  
  // Entity linking
  entityType?: 'well' | 'ticket' | 'route' | 'user' | 'invoice' | 'alert' | 'assignment' | 'coverage' | 'other';
  entityId?: string;
  entityName?: string;
  
  // Actions
  actions?: NotificationAction[];
  
  // Grouping (batch related notifications)
  groupKey?: string;           // Group related notifications together
  batchCount?: number;         // If grouped, how many notifications in batch
  
  // Metadata
  data?: Record<string, unknown>;  // Additional context
  imageUrl?: string;           // Optional image for notification
  
  // Audit
  createdAt: Date;
  createdBy?: string;
  updatedAt?: Date;
  expiresAt?: Date;            // Auto-delete after this date
}

/**
 * Data required to create new notification
 */
export interface CreateNotificationData {
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message: string;
  userId: string;
  channels?: DeliveryChannel[];
  entityType?: 'well' | 'ticket' | 'route' | 'user' | 'invoice' | 'alert' | 'assignment' | 'coverage' | 'other';
  entityId?: string;
  entityName?: string;
  actions?: NotificationAction[];
  groupKey?: string;
  data?: Record<string, unknown>;
  imageUrl?: string;
  expiresAt?: Date;
  createdBy?: string;
}

/**
 * Data for updating existing notification
 */
export interface UpdateNotificationData {
  isRead?: boolean;
  readAt?: Date;
  deliveryStatus?: DeliveryStatus[];
  data?: Record<string, unknown>;
}

/**
 * Options for listing/filtering notifications
 */
export interface NotificationListOptions {
  userId?: string;
  type?: NotificationType | NotificationType[];
  priority?: NotificationPriority | NotificationPriority[];
  isRead?: boolean;
  entityType?: string;
  entityId?: string;
  groupKey?: string;
  createdAfter?: Date;
  createdBefore?: Date;
  orderBy?: 'createdAt' | 'priority' | 'readAt';
  orderDirection?: 'asc' | 'desc';
  limit?: number;
}

/**
 * User notification preferences
 */
export interface NotificationPreferences {
  userId: string;
  companyId: string;
  channels: {
    in_app: boolean;
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  types: {
    [key in NotificationType]?: {
      enabled: boolean;
      channels: DeliveryChannel[];
    };
  };
  quietHours?: {
    enabled: boolean;
    startTime: string;  // HH:MM format
    endTime: string;    // HH:MM format
  };
  batchNotifications?: boolean;
  updatedAt: Date;
}

// ============================================================================
// ERROR CLASSES
// ============================================================================

export class NotificationServiceError extends Error {
  constructor(message: string, public cause?: Error | unknown) {
    super(message);
    this.name = 'NotificationServiceError';
  }
}

export class NotificationNotFoundError extends NotificationServiceError {
  constructor(notificationId: string) {
    super(`Notification not found: ${notificationId}`);
    this.name = 'NotificationNotFoundError';
  }
}

export class TenantValidationError extends NotificationServiceError {
  constructor(message: string = 'Tenant validation failed') {
    super(message);
    this.name = 'TenantValidationError';
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Validates tenant context is set and not default
 */
function validateCompanyContext(companyId: string): void {
  if (!companyId || companyId === 'default') {
    throw new TenantValidationError('Invalid tenant context. Cannot perform notification operations without valid tenant.');
  }
}

/**
 * Converts Firestore Timestamp fields to Date objects
 */
function convertTimestamps(data: Record<string, unknown>): Notification {
  return {
    ...data,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    readAt: (data.readAt as { toDate?: () => Date })?.toDate?.(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.(),
    expiresAt: (data.expiresAt as { toDate?: () => Date })?.toDate?.(),
    deliveryStatus: ((data.deliveryStatus as Array<Record<string, unknown>>) || []).map((ds) => ({
      ...ds,
      sentAt: (ds.sentAt as { toDate?: () => Date })?.toDate?.(),
      deliveredAt: (ds.deliveredAt as { toDate?: () => Date })?.toDate?.()
    }))
  } as Notification;
}

/**
 * Gets collection reference for tenant
 */
function getNotificationsCollection(companyId: string) {
  return collection(db, `companies/${companyId}/notifications`);
}

// ============================================================================
// CORE SERVICE FUNCTIONS
// ============================================================================

/**
 * Creates a new notification
 * 
 * @param notificationData - Notification creation data
 * @returns Created notification with ID
 * @throws {TenantValidationError} If tenant context invalid
 * @throws {NotificationServiceError} If creation fails
 * 
 * @example
 * ```typescript
 * const notification = await createNotification({
 *   type: 'ticket_assigned',
 *   priority: 'high',
 *   title: 'Service Ticket Assigned',
 *   message: 'TKT-2025-00123 assigned to you',
 *   userId: 'user_456',
 *   channels: ['in_app', 'push'],
 *   entityType: 'ticket',
 *   entityId: 'ticket_123',
 *   actions: [{
 *     label: 'View Ticket',
 *     action: 'navigate',
 *     url: '/tickets/ticket_123'
 *   }]
 * });
 * ```
 */
export async function createNotification(notificationData: CreateNotificationData): Promise<Notification> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    // Validation
    if (!notificationData.title?.trim()) {
      throw new NotificationServiceError('Notification title is required');
    }
    if (!notificationData.message?.trim()) {
      throw new NotificationServiceError('Notification message is required');
    }
    if (!notificationData.userId?.trim()) {
      throw new NotificationServiceError('User ID is required');
    }

    // Generate notification ID
    const notificationsRef = getNotificationsCollection(companyId);
    const newNotificationRef = doc(notificationsRef);

    // Build notification object
    const now = Timestamp.now();
    const channels = notificationData.channels || ['in_app'];
    
    const notification: Omit<Notification, 'id'> = {
      companyId,
      type: notificationData.type,
      priority: notificationData.priority || 'normal',
      title: notificationData.title.trim(),
      message: notificationData.message.trim(),
      userId: notificationData.userId,
      recipientId: notificationData.userId,
      channels,
      deliveryStatus: channels.map(channel => ({
        channel,
        sent: channel === 'in_app', // In-app is instant
        sentAt: channel === 'in_app' ? now.toDate() : undefined
      })),
      isRead: false,
      entityType: notificationData.entityType,
      entityId: notificationData.entityId,
      entityName: notificationData.entityName,
      actions: notificationData.actions,
      groupKey: notificationData.groupKey,
      data: notificationData.data,
      imageUrl: notificationData.imageUrl,
      createdAt: now.toDate(),
      createdBy: notificationData.createdBy,
      expiresAt: notificationData.expiresAt
    };

    // Save to Firestore
    await guardedCreate(
      FIRESTORE_PATHS.notifications(companyId),
      newNotificationRef.id,
      {
        ...notification,
        createdAt: now,
        expiresAt: notificationData.expiresAt ? Timestamp.fromDate(notificationData.expiresAt) : null
      }
    );

    return {
      id: newNotificationRef.id,
      ...notification
    };

  } catch (error: unknown) {
    if (error instanceof NotificationServiceError) {
      throw error;
    }
    throw new NotificationServiceError('Failed to create notification', error);
  }
}

/**
 * Gets a single notification by ID
 * 
 * @param notificationId - Notification document ID
 * @param companyId - Optional tenant ID (defaults to current tenant)
 * @returns Notification or null if not found
 * @throws {TenantValidationError} If tenant context invalid
 * 
 * @example
 * ```typescript
 * const notification = await getNotificationById('notif_123');
 * if (notification) {
 *   console.log(`Notification: ${notification.title} - ${notification.isRead ? 'Read' : 'Unread'}`);
 * }
 * ```
 */
export async function getNotificationById(notificationId: string, companyId?: string): Promise<Notification | null> {
  try {
    const effectiveCompanyId = companyId || getCompanyId();
    validateCompanyContext(effectiveCompanyId);

    const notificationRef = doc(db, `companies/${effectiveCompanyId}/notifications/${notificationId}`);
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      return null;
    }
    const data = notificationSnap.data();
    
    // SuperAdmin can access any tenant's notification
    if (!isSuperAdmin(data) && data.companyId !== effectiveCompanyId) {
      return null;
    }

    return convertTimestamps({
      id: notificationSnap.id,
      ...data
    });

  } catch (error: unknown) {
    console.error('Error getting notification:', error);
    throw new NotificationServiceError('Failed to get notification', error);
  }
}

/**
 * Gets list of notifications with filtering
 * 
 * @param options - Filter and pagination options
 * @returns Array of notifications matching criteria
 * @throws {TenantValidationError} If tenant context invalid
 * 
 * @example
 * ```typescript
 * // Get unread notifications for a user
 * const unread = await getNotifications({
 *   userId: 'user_123',
 *   isRead: false,
 *   orderBy: 'createdAt',
 *   limit: 20
 * });
 * 
 * // Get all ticket notifications
 * const ticketNotifs = await getNotifications({
 *   type: ['ticket_assigned', 'ticket_updated', 'ticket_resolved'],
 *   userId: 'user_123'
 * });
 * ```
 */
export async function getNotifications(options: NotificationListOptions = {}): Promise<Notification[]> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const notificationsRef = getNotificationsCollection(companyId);
    const constraints: QueryConstraint[] = [];

    // CRITICAL: Always filter by tenant
    constraints.push(where('companyId', '==', companyId));

    // User filtering
    if (options.userId) {
      constraints.push(where('userId', '==', options.userId));
    }

    // Type filtering
    if (options.type) {
      if (Array.isArray(options.type)) {
        constraints.push(where('type', 'in', options.type));
      } else {
        constraints.push(where('type', '==', options.type));
      }
    }

    // Priority filtering
    if (options.priority) {
      if (Array.isArray(options.priority)) {
        constraints.push(where('priority', 'in', options.priority));
      } else {
        constraints.push(where('priority', '==', options.priority));
      }
    }

    // Read status filtering
    if (options.isRead !== undefined) {
      constraints.push(where('isRead', '==', options.isRead));
    }

    // Entity filtering
    if (options.entityType) {
      constraints.push(where('entityType', '==', options.entityType));
    }
    if (options.entityId) {
      constraints.push(where('entityId', '==', options.entityId));
    }
    if (options.groupKey) {
      constraints.push(where('groupKey', '==', options.groupKey));
    }

    // Date range filtering
    if (options.createdAfter) {
      constraints.push(where('createdAt', '>=', Timestamp.fromDate(options.createdAfter)));
    }
    if (options.createdBefore) {
      constraints.push(where('createdAt', '<=', Timestamp.fromDate(options.createdBefore)));
    }

    // Ordering
    const orderField = options.orderBy || 'createdAt';
    const orderDir = options.orderDirection || 'desc';
    constraints.push(orderBy(orderField, orderDir));

    // Limit
    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(notificationsRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => 
      convertTimestamps({
        id: doc.id,
        ...doc.data()
      })
    );

  } catch (error: unknown) {
    console.error('Error listing notifications:', error);
    throw new NotificationServiceError('Failed to list notifications', error);
  }
}

/**
 * Marks a notification as read
 * 
 * @param notificationId - Notification ID
 * @param userId - User ID who read it (for validation)
 * @returns Updated notification
 * @throws {NotificationNotFoundError} If notification not found
 * @throws {TenantValidationError} If tenant mismatch
 * 
 * @example
 * ```typescript
 * await markAsRead('notif_123', 'user_456');
 * ```
 */
export async function markAsRead(notificationId: string, userId: string): Promise<Notification> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const notificationRef = doc(db, `companies/${companyId}/notifications/${notificationId}`);
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      throw new NotificationNotFoundError(notificationId);
    }

    const data = notificationSnap.data();
    if (data.companyId !== companyId) {
      throw new TenantValidationError('Notification does not belong to current tenant');
    }

    // Validate user owns this notification (superadmin check removed - no getCurrentUser available)
    if (data.userId !== userId) {
      throw new NotificationServiceError('Cannot mark notification as read for another user');
    }

    const now = Timestamp.now();
    await guardedUpdate(
      FIRESTORE_PATHS.notifications(companyId),
      notificationId,
      {
        isRead: true,
        readAt: now,
        updatedAt: now
      }
    );

    return convertTimestamps({
      id: notificationId,
      ...data,
      isRead: true,
      readAt: now.toDate(),
      updatedAt: now.toDate()
    });

  } catch (error: unknown) {
    if (error instanceof NotificationServiceError) {
      throw error;
    }
    throw new NotificationServiceError('Failed to mark notification as read', error);
  }
}

/**
 * Marks all notifications as read for a user
 * 
 * @param userId - User ID
 * @returns Number of notifications marked as read
 * @throws {TenantValidationError} If tenant context invalid
 * 
 * @example
 * ```typescript
 * const count = await markAllAsRead('user_123');
 * console.log(`Marked ${count} notifications as read`);
 * ```
 */
export async function markAllAsRead(userId: string): Promise<number> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    // Get unread notifications
    const unread = await getNotifications({
      userId,
      isRead: false
    });

    if (unread.length === 0) {
      return 0;
    }

    // Batch update
    const batch = writeBatch(db);
    const now = Timestamp.now();

    for (const notification of unread) {
      const ref = doc(db, `companies/${companyId}/notifications/${notification.id}`);
      batch.update(ref, {
        isRead: true,
        readAt: now,
        updatedAt: now
      });
    }

    await batch.commit();
    return unread.length;

  } catch (error: unknown) {
    console.error('Error marking all as read:', error);
    throw new NotificationServiceError('Failed to mark all notifications as read', error);
  }
}

/**
 * Deletes a notification (hard delete - permanent removal)
 * 
 * @param notificationId - Notification ID
 * @returns True if deleted
 * @throws {NotificationNotFoundError} If notification not found
 * @throws {TenantValidationError} If tenant mismatch
 * 
 * @example
 * ```typescript
 * await deleteNotification('notif_123');
 * ```
 */
export async function deleteNotification(notificationId: string): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const notificationRef = doc(db, `companies/${companyId}/notifications/${notificationId}`);
    const notificationSnap = await getDoc(notificationRef);

    if (!notificationSnap.exists()) {
      throw new NotificationNotFoundError(notificationId);
    }

    const data = notificationSnap.data();
    
    // Validate tenant ownership before deletion
    if (data.companyId !== companyId) {
      throw new TenantValidationError('Notification does not belong to current tenant');
    }

    // Use guardedDelete for secure tenant-isolated deletion
    await guardedDelete(
      FIRESTORE_PATHS.notifications(companyId),
      notificationId
    );
    
    return true;

  } catch (error: unknown) {
    if (error instanceof NotificationServiceError) {
      throw error;
    }
    throw new NotificationServiceError('Failed to delete notification', error);
  }
}

/**
 * Deletes old read notifications (cleanup)
 * 
 * @param daysOld - Delete notifications read more than X days ago
 * @param userId - Optional user ID to scope cleanup
 * @returns Number of notifications deleted
 * @throws {TenantValidationError} If tenant context invalid
 * 
 * @example
 * ```typescript
 * // Delete read notifications older than 30 days
 * const count = await deleteOldNotifications(30);
 * console.log(`Deleted ${count} old notifications`);
 * ```
 */
export async function deleteOldNotifications(daysOld: number = 30, userId?: string): Promise<number> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const oldNotifications = await getNotifications({
      isRead: true,
      createdBefore: cutoffDate,
      userId
    });

    if (oldNotifications.length === 0) {
      return 0;
    }

    // Batch delete
    const batch = writeBatch(db);

    for (const notification of oldNotifications) {
      const ref = doc(db, `companies/${companyId}/notifications/${notification.id}`);
      batch.delete(ref);
    }

    await batch.commit();
    return oldNotifications.length;

  } catch (error: unknown) {
    console.error('Error deleting old notifications:', error);
    throw new NotificationServiceError('Failed to delete old notifications', error);
  }
}

/**
 * Gets count of unread notifications for a user
 * 
 * @param userId - User ID
 * @returns Count of unread notifications
 * 
 * @example
 * ```typescript
 * const count = await getUnreadCount('user_123');
 * console.log(`You have ${count} unread notifications`);
 * ```
 */
export async function getUnreadCount(userId: string): Promise<number> {
  try {
    const notifications = await getNotifications({
      userId,
      isRead: false
    });
    return notifications.length;
  } catch (error: unknown) {
    console.error('Error getting unread count:', error);
    return 0;
  }
}

/**
 * Gets user notification preferences
 * 
 * @param userId - User ID
 * @returns User preferences or default preferences
 * 
 * @example
 * ```typescript
 * const prefs = await getUserPreferences('user_123');
 * if (prefs.channels.email) {
 *   console.log('User has email notifications enabled');
 * }
 * ```
 */
export async function getUserPreferences(userId: string): Promise<NotificationPreferences> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const prefsRef = doc(db, `companies/${companyId}/notificationPreferences/${userId}`);
    const prefsSnap = await getDoc(prefsRef);

    if (prefsSnap.exists()) {
      const data = prefsSnap.data();
      return {
        ...data,
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as NotificationPreferences;
    }

    // Return defaults
    return {
      userId,
      companyId,
      channels: {
        in_app: true,
        email: true,
        sms: false,
        push: true
      },
      types: {},
      batchNotifications: false,
      updatedAt: new Date()
    };

  } catch (error: unknown) {
    console.error('Error getting user preferences:', error);
    throw new NotificationServiceError('Failed to get user preferences', error);
  }
}

/**
 * Updates user notification preferences
 * 
 * @param userId - User ID
 * @param preferences - Partial preferences to update
 * @returns Updated preferences
 * 
 * @example
 * ```typescript
 * await updateUserPreferences('user_123', {
 *   channels: {
 *     in_app: true,
 *     email: true,
 *     sms: false,
 *     push: false
 *   },
 *   batchNotifications: true
 * });
 * ```
 */
export async function updateUserPreferences(
  userId: string,
  preferences: Partial<Omit<NotificationPreferences, 'userId' | 'companyId' | 'updatedAt'>>
): Promise<NotificationPreferences> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const now = Timestamp.now();

    // Build the preferences update payload with required fields
    // Use Record type for Firestore compatibility
    const updateData: Record<string, unknown> = {
      ...preferences,
      userId,
      companyId,
      updatedAt: now,
    };

    // Use guardedCreate with merge for upsert behavior (create if not exists, merge if exists)
    // This is the correct pattern for notification preferences which may or may not exist
    await guardedCreate(
      FIRESTORE_PATHS.notificationPreferences(companyId),
      userId,
      updateData,
      { merge: true }
    );

    return {
      ...updateData,
      updatedAt: now.toDate()
    } as NotificationPreferences;

  } catch (error: unknown) {
    console.error('Error updating user preferences:', error);
    throw new NotificationServiceError('Failed to update user preferences', error);
  }
}

// ============================================================================
// WELL ASSIGNMENT NOTIFICATIONS (Tasks 65-75)
// ============================================================================

/**
 * Notify a pumper when they are assigned to a well
 * Task #65
 */
export async function notifyPumperOfAssignment(
  pumperId: string,
  _pumperName: string,
  well: { id: string; name: string },
  options: {
    reason?: string;
    performedBy: string;
    performedByName: string;
    assignmentType?: string;
  }
): Promise<Notification> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  return createNotification({
    type: 'well_assigned',
    priority: 'normal',
    title: `Assigned to ${well.name}`,
    message: options.reason 
      ? `You have been assigned to ${well.name} by ${options.performedByName}. Reason: ${options.reason}`
      : `You have been assigned to ${well.name} by ${options.performedByName}.`,
    userId: pumperId,
    channels: ['in_app', 'push'],
    entityType: 'well',
    entityId: well.id,
    entityName: well.name,
    data: { 
      assignedBy: options.performedBy, 
      assignedByName: options.performedByName, 
      reason: options.reason,
      assignmentType: options.assignmentType,
    },
    createdBy: options.performedBy,
  });
}

/**
 * Notify a pumper when they are unassigned from a well
 * Task #66
 */
export async function notifyPumperOfUnassignment(
  pumperId: string,
  _pumperName: string,
  well: { id: string; name: string },
  options: {
    reason?: string;
    performedBy: string;
    performedByName: string;
  }
): Promise<Notification> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  return createNotification({
    type: 'well_unassigned',
    priority: 'normal',
    title: `Unassigned from ${well.name}`,
    message: options.reason 
      ? `You have been unassigned from ${well.name} by ${options.performedByName}. Reason: ${options.reason}`
      : `You have been unassigned from ${well.name} by ${options.performedByName}.`,
    userId: pumperId,
    channels: ['in_app'],
    entityType: 'well',
    entityId: well.id,
    entityName: well.name,
    data: { 
      unassignedBy: options.performedBy, 
      unassignedByName: options.performedByName, 
      reason: options.reason,
    },
    createdBy: options.performedBy,
  });
}

/**
 * Notify a pumper when they are assigned to multiple wells at once
 * Task #67
 */
export async function notifyPumperOfBulkAssignment(
  pumperId: string,
  _pumperName: string,
  wells: Array<{ wellId: string; wellName: string; success: boolean }>,
  options: {
    reason?: string;
    performedBy: string;
    performedByName: string;
    assignmentType?: string;
  }
): Promise<Notification> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  const successfulWells = wells.filter(w => w.success);
  const count = successfulWells.length;
  const displayNames = successfulWells.slice(0, 3).map(w => w.wellName).join(', ');
  const more = count > 3 ? ` (+${count - 3} more)` : '';
  
  return createNotification({
    type: 'bulk_wells_assigned',
    priority: 'normal',
    title: `Assigned to ${count} well${count !== 1 ? 's' : ''}`,
    message: options.reason 
      ? `You have been assigned to ${displayNames}${more} by ${options.performedByName}. Reason: ${options.reason}`
      : `You have been assigned to ${displayNames}${more} by ${options.performedByName}.`,
    userId: pumperId,
    channels: ['in_app', 'push'],
    entityType: 'assignment',
    groupKey: `bulk_assign_${Date.now()}`,
    data: { 
      wellIds: successfulWells.map(w => w.wellId), 
      wellNames: successfulWells.map(w => w.wellName), 
      assignedBy: options.performedBy, 
      assignedByName: options.performedByName, 
      reason: options.reason, 
      count,
      assignmentType: options.assignmentType,
    },
    createdBy: options.performedBy,
  });
}

/**
 * Notify both pumpers when temporary coverage starts
 * Task #68
 */
export async function notifyCoverageStarted(
  originalPumperId: string,
  originalPumperName: string,
  coveringPumperId: string,
  coveringPumperName: string,
  wells: Array<{ id: string; name: string }>,
  endDate: Date | null,
  reason?: string
): Promise<Notification[]> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  const count = wells.length;
  const displayNames = wells.slice(0, 3).map(w => w.name).join(', ');
  const more = count > 3 ? ` (+${count - 3} more)` : '';
  const endInfo = endDate ? ` until ${endDate.toLocaleDateString()}` : '';
  const wellNames = wells.map(w => w.name);
  const wellIds = wells.map(w => w.id);
  
  const notifications = await Promise.all([
    // Notify covering pumper
    createNotification({
      type: 'coverage_started',
      priority: 'high',
      title: `Coverage Started for ${originalPumperName}`,
      message: `You are now covering ${displayNames}${more}${endInfo}.${reason ? ` Reason: ${reason}` : ''}`,
      userId: coveringPumperId,
      channels: ['in_app', 'push'],
      entityType: 'coverage',
      data: { originalPumperId, originalPumperName, wellNames, wellIds, endDate: endDate?.toISOString(), reason },
      createdBy: 'system',
    }),
    // Notify original pumper
    createNotification({
      type: 'coverage_started',
      priority: 'normal',
      title: `${coveringPumperName} is Covering Your Wells`,
      message: `${coveringPumperName} will cover ${displayNames}${more}${endInfo}.${reason ? ` Reason: ${reason}` : ''}`,
      userId: originalPumperId,
      channels: ['in_app', 'push'],
      entityType: 'coverage',
      data: { coveringPumperId, coveringPumperName, wellNames, wellIds, endDate: endDate?.toISOString(), reason },
      createdBy: 'system',
    }),
  ]);
  
  return notifications;
}

/**
 * Notify both pumpers when temporary coverage ends
 * Task #70
 */
export async function notifyCoverageEnded(
  originalPumperId: string,
  originalPumperName: string,
  coveringPumperId: string,
  coveringPumperName: string,
  wells: Array<{ id: string; name: string }>
): Promise<Notification[]> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  const count = wells.length;
  const displayNames = wells.slice(0, 3).map(w => w.name).join(', ');
  const more = count > 3 ? ` (+${count - 3} more)` : '';
  const wellNames = wells.map(w => w.name);
  const wellIds = wells.map(w => w.id);
  
  const notifications = await Promise.all([
    // Notify covering pumper
    createNotification({
      type: 'coverage_ended',
      priority: 'normal',
      title: `Coverage Ended for ${originalPumperName}`,
      message: `Your coverage of ${displayNames}${more} has ended.`,
      userId: coveringPumperId,
      channels: ['in_app'],
      entityType: 'coverage',
      data: { originalPumperId, originalPumperName, wellNames, wellIds },
      createdBy: 'system',
    }),
    // Notify original pumper
    createNotification({
      type: 'coverage_ended',
      priority: 'high',
      title: `Coverage Ended - Wells Returned`,
      message: `${coveringPumperName} is no longer covering ${displayNames}${more}. Your wells are back.`,
      userId: originalPumperId,
      channels: ['in_app', 'push'],
      entityType: 'coverage',
      data: { coveringPumperId, coveringPumperName, wellNames, wellIds },
      createdBy: 'system',
    }),
  ]);
  
  return notifications;
}

/**
 * Notify both pumpers when backup is activated
 * Task #72
 */
export async function notifyBackupActivated(
  backupPumperId: string,
  backupPumperName: string,
  primaryPumperId: string,
  primaryPumperName: string,
  wells: Array<{ id: string; name: string }>,
  reason?: string
): Promise<Notification[]> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  const count = wells.length;
  const displayNames = wells.slice(0, 3).map(w => w.name).join(', ');
  const more = count > 3 ? ` (+${count - 3} more)` : '';
  const wellIds = wells.map(w => w.id);
  const wellNames = wells.map(w => w.name);
  
  const notifications = await Promise.all([
    // Notify backup pumper
    createNotification({
      type: 'backup_activated',
      priority: 'high',
      title: `Backup Activated: ${displayNames}${more}`,
      message: `You are now covering ${displayNames}${more} as backup for ${primaryPumperName}.${reason ? ` Reason: ${reason}` : ''}`,
      userId: backupPumperId,
      channels: ['in_app', 'push'],
      entityType: 'well',
      entityId: wells[0]?.id,
      entityName: wells[0]?.name,
      data: { primaryPumperId, primaryPumperName, wellIds, wellNames, reason },
      createdBy: 'system',
    }),
    // Notify primary pumper
    createNotification({
      type: 'backup_activated',
      priority: 'normal',
      title: `Backup Covering ${displayNames}${more}`,
      message: `${backupPumperName} is now covering ${displayNames}${more} as your backup.${reason ? ` Reason: ${reason}` : ''}`,
      userId: primaryPumperId,
      channels: ['in_app'],
      entityType: 'well',
      entityId: wells[0]?.id,
      entityName: wells[0]?.name,
      data: { backupPumperId, backupPumperName, wellIds, wellNames, reason },
      createdBy: 'system',
    }),
  ]);
  
  return notifications;
}

/**
 * Notify both pumpers when backup is deactivated
 * Task #73
 */
export async function notifyBackupDeactivated(
  backupPumperId: string,
  backupPumperName: string,
  primaryPumperId: string,
  primaryPumperName: string,
  wells: Array<{ id: string; name: string }>,
  reason?: string
): Promise<Notification[]> {
  const companyId = getCompanyId();
  validateCompanyContext(companyId);
  
  const count = wells.length;
  const displayNames = wells.slice(0, 3).map(w => w.name).join(', ');
  const more = count > 3 ? ` (+${count - 3} more)` : '';
  const wellIds = wells.map(w => w.id);
  const wellNames = wells.map(w => w.name);
  
  const notifications = await Promise.all([
    // Notify backup pumper
    createNotification({
      type: 'backup_deactivated',
      priority: 'normal',
      title: `Backup Deactivated: ${displayNames}${more}`,
      message: `You are no longer covering ${displayNames}${more}. ${primaryPumperName} has resumed primary responsibility.`,
      userId: backupPumperId,
      channels: ['in_app'],
      entityType: 'well',
      entityId: wells[0]?.id,
      entityName: wells[0]?.name,
      data: { primaryPumperId, primaryPumperName, wellIds, wellNames, reason },
      createdBy: 'system',
    }),
    // Notify primary pumper
    createNotification({
      type: 'backup_deactivated',
      priority: 'normal',
      title: `You're Back on ${displayNames}${more}`,
      message: `${backupPumperName} is no longer covering ${displayNames}${more}. You have resumed primary responsibility.`,
      userId: primaryPumperId,
      channels: ['in_app'],
      entityType: 'well',
      entityId: wells[0]?.id,
      entityName: wells[0]?.name,
      data: { backupPumperId, backupPumperName, wellIds, wellNames, reason },
      createdBy: 'system',
    }),
  ]);
  
  return notifications;
}

// ============================================================================
// ORPHAN WELLS NOTIFICATION (MANAGER ALERT WITH THROTTLING)
// ============================================================================

/** 
 * Throttle key for orphan wells notifications in Firestore metadata 
 * Stored per company to prevent cross-tenant interference
 */
const ORPHAN_ALERT_THROTTLE_KEY = 'orphanWellsAlertLastSent';

/** Minimum hours between orphan well notifications (24 hours = once per day) */
const ORPHAN_ALERT_COOLDOWN_HOURS = 24;

/**
 * Check if orphan well alert can be sent (throttle check)
 * Prevents notification spam by enforcing once-per-day cooldown per company.
 * 
 * @returns True if alert can be sent, false if within cooldown period
 */
export async function canSendOrphanWellsAlert(): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    
    // Check metadata doc for last sent timestamp
    const metadataRef = doc(db, `companies/${companyId}/metadata/orphanAlerts`);
    const metadataSnap = await getDoc(metadataRef);
    
    if (!metadataSnap.exists()) {
      // Never sent before - can send
      return true;
    }
    
    const data = metadataSnap.data();
    const lastSent = data[ORPHAN_ALERT_THROTTLE_KEY];
    
    if (!lastSent) {
      return true;
    }
    
    // Calculate hours since last alert
    const lastSentDate = lastSent.toDate ? lastSent.toDate() : new Date(lastSent);
    const hoursSinceLastAlert = (Date.now() - lastSentDate.getTime()) / (1000 * 60 * 60);
    
    return hoursSinceLastAlert >= ORPHAN_ALERT_COOLDOWN_HOURS;
    
  } catch (error) {
    console.warn('Failed to check orphan alert throttle:', error);
    // On error, allow sending (fail-open for critical alerts)
    return true;
  }
}

/**
 * Updates the last-sent timestamp for orphan well alerts.
 * Used for throttling to prevent notification spam (once per 24 hours max).
 * 
 * @internal Called after successfully sending orphan well notifications
 */
export async function updateOrphanAlertTimestamp(): Promise<void> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    
    // Use guardedUpdate for tenant-isolated metadata updates
    await guardedUpdate(
      FIRESTORE_PATHS.metadata(companyId),
      'orphanAlerts',
      {
        [ORPHAN_ALERT_THROTTLE_KEY]: Timestamp.now(),
        companyId,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
    
  } catch (error) {
    console.error('Failed to update orphan alert timestamp:', error);
    // Non-fatal - notification was still sent, throttle just won't work properly
  }
}

/**
 * Get the last time an orphan wells alert was sent for this company
 * @returns Date of last alert or null if never sent
 */
export async function getLastOrphanAlertTime(): Promise<Date | null> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    
    const metadataRef = doc(db, `companies/${companyId}/metadata/orphanAlerts`);
    const metadataSnap = await getDoc(metadataRef);
    
    if (!metadataSnap.exists()) {
      return null;
    }
    
    const data = metadataSnap.data();
    const lastSent = data[ORPHAN_ALERT_THROTTLE_KEY];
    
    if (!lastSent) {
      return null;
    }
    
    return lastSent.toDate ? lastSent.toDate() : new Date(lastSent);
    
  } catch (error) {
    console.warn('Failed to get last orphan alert time:', error);
    return null;
  }
}

/**
 * Notifies managers when orphan wells are detected.
 * 
 * THROTTLING: Sends at most once per 24 hours per company to prevent notification spam.
 * Stores last-notified timestamp in Firestore metadata.
 * 
 * @param managerIds - Array of manager user IDs to notify
 * @param orphanWells - Array of orphan well info objects with id and name
 * @param options - Optional configuration
 * @returns True if notifications were sent, false if throttled or failed
 * 
 * @example
 * ```typescript
 * // Notify managers about orphan wells
 * const sent = await notifyOrphanWellsDetected(
 *   ['manager_123', 'manager_456'],
 *   [{ id: 'well_1', name: 'Alpha-5' }, { id: 'well_2', name: 'Beta-7' }]
 * );
 * 
 * if (sent) {
 *   console.log('Managers notified about orphan wells');
 * } else {
 *   console.log('Notification throttled or failed');
 * }
 * ```
 */
export async function notifyOrphanWellsDetected(
  managerIds: string[],
  orphanWells: Array<{ id: string; name: string }>,
  options: {
    /** Force send even if within cooldown period (for critical alerts) */
    force?: boolean;
    /** Priority level (defaults to 'high' for orphan alerts) */
    priority?: NotificationPriority;
    /** Include emergency tag if any wells are 7+ days unassigned */
    hasEmergency?: boolean;
  } = {}
): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    
    // Validate inputs
    if (!managerIds || managerIds.length === 0) {
      console.warn('notifyOrphanWellsDetected: No manager IDs provided');
      return false;
    }
    
    if (!orphanWells || orphanWells.length === 0) {
      console.info('notifyOrphanWellsDetected: No orphan wells to report');
      return false;
    }
    
    // Check throttle (unless forced)
    if (!options.force) {
      const canSend = await canSendOrphanWellsAlert();
      if (!canSend) {
        console.info('notifyOrphanWellsDetected: Throttled (within 24h cooldown)');
        return false;
      }
    }
    
    // Build notification content
    const wellCount = orphanWells.length;
    const wellNames = orphanWells.slice(0, 3).map(w => w.name).join(', ');
    const moreCount = wellCount > 3 ? ` (+${wellCount - 3} more)` : '';
    
    const priority = options.priority || (options.hasEmergency ? 'urgent' : 'high');
    const emoji = options.hasEmergency ? '🚨' : '⚠️';
    
    const title = options.hasEmergency
      ? `${emoji} URGENT: ${wellCount} Well${wellCount !== 1 ? 's' : ''} Without Pumper`
      : `${emoji} ${wellCount} Unassigned Well${wellCount !== 1 ? 's' : ''} Need Attention`;
    
    const message = options.hasEmergency
      ? `${wellCount} well${wellCount !== 1 ? 's are' : ' is'} without an assigned pumper and may be missing production checks. Priority wells: ${wellNames}${moreCount}. Immediate assignment required.`
      : `${wellCount} well${wellCount !== 1 ? 's are' : ' is'} currently unassigned: ${wellNames}${moreCount}. Please review and assign pumpers to ensure production coverage.`;
    
    // Send notification to each manager
    const notifications: Promise<Notification>[] = managerIds.map(managerId =>
      createNotification({
        type: 'orphan_wells_detected',
        priority,
        title,
        message,
        userId: managerId,
        channels: ['in_app', 'push'],
        entityType: 'well',
        groupKey: `orphan_wells_${companyId}_${new Date().toISOString().split('T')[0]}`,
        data: {
          orphanCount: wellCount,
          wellIds: orphanWells.map(w => w.id),
          wellNames: orphanWells.map(w => w.name),
          hasEmergency: options.hasEmergency || false,
          reportedAt: new Date().toISOString(),
        },
        actions: [
          {
            label: 'Review & Assign',
            action: 'navigate',
            url: '/manager/well-assignments?filter=orphan',
          },
        ],
        createdBy: 'system',
      })
    );
    
    // Wait for all notifications
    const results = await Promise.allSettled(notifications);
    
    // Count successes
    const successCount = results.filter(r => r.status === 'fulfilled').length;
    const failCount = results.filter(r => r.status === 'rejected').length;
    
    if (failCount > 0) {
      console.warn(`notifyOrphanWellsDetected: ${failCount}/${managerIds.length} notifications failed`);
    }
    
    // Update throttle timestamp if at least one notification succeeded
    if (successCount > 0) {
      await updateOrphanAlertTimestamp();
      console.info(`✅ Orphan well alert sent to ${successCount} manager(s) for ${wellCount} well(s)`);
      return true;
    }
    
    return false;
    
  } catch (error: unknown) {
    console.error('notifyOrphanWellsDetected failed:', error);
    throw new NotificationServiceError('Failed to send orphan wells notification', error);
  }
}
